import React, { Fragment, useEffect, useRef, useState } from 'react';

import { ButtonIconOnly, IMessage, ReactTreeAccordion, TreeNodeInArray } from '@netfront/ui-library';
import noop from 'lodash.noop';
import orderBy from 'lodash.orderby';

import { DEFAULT_API_ERROR_MESSAGE, DEFAULT_MESSAGE } from '../QuestionSnippet.constants';
import { handleApolloError } from '../QuestionSnippet.handlers';

import {
  getAccordionData,
  getAvailableResponseNumber,
  getFirstUnansweredAvailableResponse,
  UnansweredAvailableResponses,
} from './QuestionAnswerMatchSnippet.helpers';
import { QuestionAnswerMatchSnippetProps } from './QuestionAnswerMatchSnippet.interfaces';

import { getContentSnippet, getContentSnippetFormId, useContentPageContext } from '../../../../contexts';
import { useSetUserAnswerMatch } from '../../../../hooks';
import { IDBMatchItem, IDBQuestionAnswerMatchResponseMatch, IDBQuestionResponse } from '../../../../interfaces';
import { DEFAULT_OPTION_VALUE, DropDownListSnippet, IDropDownListOption } from '../../../Snippets/DropDownListSnippet';
import { IRadioOption, RadioButtonSnippet } from '../../../Snippets/RadioButtonSnippet';
import { IUserGeneratedResponseOnSaveCompletedResponse, UserGeneratedResponse } from '../../../UserGeneratedResponse';
import { getMutationBaseVariables } from '../../Snippets.helpers';

import './QuestionAnswerMatchSnippet.css';

const QuestionAnswerMatchSnippet = ({
  accessToken,
  contentSnippetId,
  contentSnippetQuestion,
  message = DEFAULT_MESSAGE,
  userFlowStepTrackId,
}: QuestionAnswerMatchSnippetProps) => {
  const { state: contentPage } = useContentPageContext();

  const contentSnippet = contentSnippetQuestion ?? getContentSnippet(contentSnippetId, contentPage);
  const contentSnippetFormId = getContentSnippetFormId(contentSnippetId, contentPage);

  const { answer, configuration, mandatory: isMandatory = false, message: contentSnippetMessage } = contentSnippet ?? {};
  const { answers = [] } = answer ?? {};
  const { matchItems, responseSet, allowUserGeneratedResponses: shouldAllowUserGeneratedResponses, layout } = configuration ?? {};
  const { availableResponses: avResponses = [], id: questionResponseSetId } = responseSet ?? {};

  const { length: totalAnswers } = answers;

  const isLayoutStackedCards = layout === 'STACKED_CARDS';
  const isLayoutList = layout === 'LIST';

  const sortedMatchItems = useRef<IDBMatchItem[]>(orderBy(matchItems, 'sort'));

  const [availableResponses, setAvailableResponses] = useState<IDBQuestionResponse[]>(avResponses);

  const { length: totalAvailableResponses } = availableResponses;

  const [accordionData, setAccordionData] = useState<TreeNodeInArray[]>();
  const [currentAnswers, setCurrentAnswers] = useState<IDBQuestionAnswerMatchResponseMatch[]>(answers);
  const [currentMessage, setCurrentMessage] = useState<IMessage>(message);
  const [firstUnansweredAvailableResponse, setFirstUnansweredAvailableResponse] = useState<IDBQuestionResponse | undefined>(
    getFirstUnansweredAvailableResponse(answers, availableResponses)
  );
  const [hasAnsweredAllAvailableResponses, setHasAnsweredAllAvailableResponses] = useState<boolean>(
    totalAnswers === totalAvailableResponses
  );

  const [matchItemDropDownOptions, setMatchItemDropDownOptions] = useState<IDropDownListOption[]>([]);
  const [matchItemRadioOptions, setMatchItemRadioOptions] = useState<IRadioOption[]>();

  const { handleSetUserAnswerMatch } = useSetUserAnswerMatch({
    onCompleted: ({ setUserAnswer: { answers: returnedAnswers } }) => {
      setCurrentAnswers(returnedAnswers);
      setFirstUnansweredAvailableResponse(getFirstUnansweredAvailableResponse(returnedAnswers, availableResponses));
      setHasAnsweredAllAvailableResponses(returnedAnswers.length === totalAvailableResponses);
    },
    onError: (error) => {
      handleApolloError(error);
      setCurrentMessage({
        error: DEFAULT_API_ERROR_MESSAGE,
        success: '',
      });
    },
    token: accessToken,
  });

  const handleMatchItemAnswered = (contentPageId: number, inputDefinedPosition: number, inputQuestionResponseMatchId: number) => {
    if (!contentSnippetFormId) {
      return;
    }

    const baseVariables = getMutationBaseVariables(contentPageId, contentSnippetFormId, contentSnippetId, Number(userFlowStepTrackId));

    handleSetUserAnswerMatch({
      ...baseVariables,
      answers: [
        ...currentAnswers
          .filter(({ questionResponseMatchId }) => inputQuestionResponseMatchId !== questionResponseMatchId)
          .map(({ definedPosition, questionResponseMatchId }) => ({
            definedPosition,
            questionResponseMatchId,
          }))
          .concat({
            definedPosition: inputDefinedPosition,
            questionResponseMatchId: inputQuestionResponseMatchId,
          }),
      ],
    });
  };

  const handleAddUserGeneratedResponseSaveCompleted = (data: IUserGeneratedResponseOnSaveCompletedResponse) => {
    if (!contentSnippetFormId) {
      return;
    }

    const { updatedAvailableResponses } = data;

    setHasAnsweredAllAvailableResponses(false);
    setAvailableResponses(updatedAvailableResponses);
    setFirstUnansweredAvailableResponse(getFirstUnansweredAvailableResponse(currentAnswers, updatedAvailableResponses));
  };

  useEffect(() => {
    if (!contentPage) {
      return;
    }

    setAccordionData(
      getAccordionData({
        answers: currentAnswers,
        availableResponses,
        matchItems: sortedMatchItems.current,
        onMatchItemsDropdownChange: (availableResponseId, definedPosition) => {
          handleMatchItemAnswered(contentPage.id, definedPosition, availableResponseId);
        },
      })
    );

    setMatchItemRadioOptions(
      sortedMatchItems.current.map(({ id: matchItemId, maxItemsCount = 0, text = '' }, matchItemIndex): IRadioOption => {
        const matchItemIdAsString = String(matchItemId);
        const matchItemAnswers = currentAnswers.filter(({ definedPosition }) => definedPosition === matchItemIndex).length;

        return {
          id: matchItemIdAsString,
          isDisabled: maxItemsCount === matchItemAnswers,
          label: text,
          value: matchItemIdAsString,
        };
      })
    );

    setMatchItemDropDownOptions(
      sortedMatchItems.current.map(({ id: matchItemId, maxItemsCount = 0, text = '' }, matchItemIndex): IRadioOption => {
        const matchItemIdAsString = String(matchItemId);
        const matchItemAnswers = currentAnswers.filter(({ definedPosition }) => definedPosition === matchItemIndex).length;

        return {
          id: matchItemIdAsString,
          isDisabled: maxItemsCount === matchItemAnswers,
          label: text,
          value: matchItemIdAsString,
        };
      })
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAnswers]);

  useEffect(() => {
    if (!contentSnippetMessage) {
      return;
    }

    setCurrentMessage(contentSnippetMessage);
  }, [contentSnippetMessage]);

  return (
    <Fragment>
      <div className="c-question-answer-match__card">
        {hasAnsweredAllAvailableResponses && (
          <Fragment>
            <div className="c-question-answer-match__card-completed">Completed!</div>
            <div className="c-question-answer-match__card-completed-icon">
              <ButtonIconOnly iconId="id_tick_icon" text="" onClick={noop} />
            </div>
          </Fragment>
        )}
        {contentSnippet && shouldAllowUserGeneratedResponses && questionResponseSetId && (
          <UserGeneratedResponse
            accessToken={accessToken}
            contentSnippetId={contentSnippet.id}
            currentAvailableResponses={availableResponses}
            questionConfigurationType="Match"
            questionResponseSetId={questionResponseSetId}
            onSaveCompleted={handleAddUserGeneratedResponseSaveCompleted}
          />
        )}
        {contentPage && matchItemRadioOptions && isLayoutList && (
          <Fragment>
            <div>
              <ul>
                {UnansweredAvailableResponses(currentAnswers, availableResponses).map((response) => (
                  <li key={`match-response-${response.id}`}>
                    <div>
                      <p>{response.label}</p>
                      <DropDownListSnippet
                        defaultOptionText="Select a response"
                        id={`c-question-answer-match__card-available-response-${response.id}-dropdownlist-button`}
                        isRequired={isMandatory}
                        message={currentMessage}
                        options={matchItemDropDownOptions}
                        selectedValue={DEFAULT_OPTION_VALUE}
                        onChange={(matchItemId) => {
                          const definedPosition = sortedMatchItems.current.findIndex(({ id }) => id === Number(matchItemId));
                          handleMatchItemAnswered(contentPage.id, definedPosition, response.id);
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Fragment>
        )}
        {contentPage && firstUnansweredAvailableResponse && matchItemRadioOptions && isLayoutStackedCards && (
          <Fragment>
            <div className="c-question-answer-match__card-counter">
              {getAvailableResponseNumber(firstUnansweredAvailableResponse, availableResponses)} of {totalAvailableResponses}
            </div>
            <div className="c-question-answer-match__card-label">{firstUnansweredAvailableResponse.label}</div>
            <RadioButtonSnippet
              id={`c-question-answer-match__card-available-response-${firstUnansweredAvailableResponse.id}-radio-button`}
              isRequired={isMandatory}
              message={currentMessage}
              options={matchItemRadioOptions}
              selectedValue=""
              onChange={(matchItemId) => {
                const definedPosition = sortedMatchItems.current.findIndex(({ id }) => id === Number(matchItemId));
                handleMatchItemAnswered(contentPage.id, definedPosition, firstUnansweredAvailableResponse.id);
              }}
            />
          </Fragment>
        )}
      </div>
      {accordionData && (
        <div className="c-question-answer-match__accordion">
          <ReactTreeAccordion data={accordionData} />
        </div>
      )}
    </Fragment>
  );
};

export { QuestionAnswerMatchSnippet };
